import type {ReactElement} from 'react';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {AJAXResponseProductData, ANALYTICS_ACTION_FOR_PUBLISH} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {DisplayType, initialOrderDetails, ProductType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {Text, TextSize} from '@Components/text';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {InputField} from '@Components/input-field-v2';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {InputControlItemType, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {ModalDetails} from '@Components/modal/modal-context';
import {processDesignNew} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-library';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {
  setErrorMessageForPublishing,
  setErrorOnPublishPanel,
  setOrderDetails,
  setProductType,
  setShowLoader,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {PublishingProgressBar} from '@Panels/poster-publishing-options-panel/components/publishing-progress-bar/publishing-progress-bar';
import {useFreebieProduct, useHashedId, usePrintProduct, useShowLoader} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import type {PublishingEmbedInWebpagePanelProps, PublishingEmbedResponseProps} from './publishing-embed-in-webpage-panel.types';
import styles from './publishing-embed-in-webpage-panel.module.scss';
import {useAppDispatch} from '@/hooks';
import {PublishingLoaderScreen} from '@Panels/poster-publishing-options-panel/components/publishing-loader-screen/publishing-loader-screen';
import {GA4EventParamName} from '@Libraries/ga-events';

export function PublishingEmbedInWebpagePanel({...props}: PublishingEmbedInWebpagePanelProps): ReactElement {
  const {displayType} = props;
  const hashedId = useHashedId();
  const [addLink, setAddLink] = useState(false);
  const [input, setInput] = useState('');
  const [oldInput, setOldInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [published, setPublished] = useState(false);
  const [htmlSnippet, setHtmlSnippet] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [updateEmbedLink, setUpdateEmbedLink] = useState(false);
  const [linkEmbeded, setLinkEmbeded] = useState(false);
  const [invalidUrl, setInvalidUrl] = useState(false);
  const [changeHtmlSnippet, setChangeHtmlSnippet] = useState(false);
  const [showLoadScreen, setShowLoadScreen] = useState(true);
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const dispatch = useAppDispatch();
  const STATUS_DOWNLOADABLE = 'downloadable';
  const hasRun = useRef(false);
  const userLevel = window.PMW.getUserPremiumLevel();

  const freebieProductData = useFreebieProduct();
  const premiumProductDataForPrint = usePrintProduct();
  const showLoader = useShowLoader();
  let productUsed: AJAXResponseProductData | undefined = userLevel === USER_PREMIUM_LEVELS.PAYG ? freebieProductData : premiumProductDataForPrint;
  if (productUsed?.hashedId === '') {
    productUsed = {...productUsed, hashedId};
  }
  const orderId: string | null = productUsed?.orderId ?? null;
  const status = productUsed?.status ?? '';
  const productId: string = productUsed?.id ?? '';
  let panelStyle;
  window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_PUBLISH, {[GA4EventParamName.TYPE]: 'Embed'});

  useEffect(() => {
    setShowLoadScreen(true);
    if (!showLoader && status === STATUS_DOWNLOADABLE) {
      void window.PMW.writeLocal('poster/showEmbedPanelNew', {hid: hashedId, pid: productId, orderID: orderId})
        .then((response: PublishingEmbedResponseProps) => {
          setHtmlSnippet(response.htmlSnippet);
          setPublished(response.isPosterEnabled);
          if (response.hasUserAddedEmbedLink) {
            setAddLink(response.hasUserAddedEmbedLink);
            setUpdateEmbedLink(true);
            if (!input.match(/^[a-zA-Z]+:\/\//)) {
              setInput(`https://${response.customizableLink}`);
            }
          }
        })
        .catch(() => {
          dispatch(setErrorOnPublishPanel(true));
          dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
        })
        .finally(() => {
          setShowLoadScreen(false);
        });
    }
  }, [showLoader, status]);

  useEffect(() => {
    if (!hasRun.current) {
      dispatch(setShowLoader(true));
      dispatch(setOrderDetails(initialOrderDetails));
      if (userLevel === USER_PREMIUM_LEVELS.PAYG) {
        dispatch(setProductType(ProductType.FREEBIE));
      } else {
        dispatch(setProductType(ProductType.PRINT));
      }
      processDesignNew(status, productUsed ?? ({} as AJAXResponseProductData), {});
      hasRun.current = true;
    }
  }, []);

  useEffect((): void => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 3000);
    }
  }, [copied]);

  const handleEmbedPublishClick = (): void => {
    setIsLoading(true);
    void window.PMW.writeLocal('poster/publishEmbedLinkNew', {hid: hashedId, orderID: orderId})
      .catch(() => {
        dispatch(setErrorOnPublishPanel(true));
        dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
      })
      .finally(() => {
        setIsLoading(false);
        setPublished(true);
      });
  };

  const handleEmbedUnpublishClick = (): void => {
    setIsLoading(true);
    void window.PMW.writeLocal('poster/unpublishEmbedLinkNew', {hid: hashedId, orderID: orderId})
      .catch(() => {
        dispatch(setErrorOnPublishPanel(true));
        dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
      })
      .finally(() => {
        setIsLoading(false);
        setPublished(false);
        setCopied(false);
      });
  };

  const handleRemoveEmbedLink = (): void => {
    void window.PMW.writeLocal('poster/removeEmbedLinkNew', {hid: hashedId, orderID: orderId, useIframe: changeHtmlSnippet ? 0 : 1})
      .then((response: PublishingEmbedResponseProps) => {
        setHtmlSnippet(response.htmlSnippet);
        setInput('');
        setUpdateEmbedLink(false);
        setInvalidUrl(false);
        setAddLink(false);
        setLinkEmbeded(false);
      })
      .catch(() => {
        dispatch(setErrorOnPublishPanel(true));
        dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
      })
      .finally(() => {});
  };

  const handleUpdateEmbedLink = (): void => {
    const maxLinkLength = 100;

    if (!window.PMW.util.isValidURLSimple(input) && input.length < maxLinkLength) {
      setInvalidUrl(true);
      return;
    }
    if (!input.match(/^[a-zA-Z]+:\/\//)) {
      setInput(`https://${input}`);
    }
    void window.PMW.writeLocal('poster/updateLinkToEmbedNew', {hid: hashedId, val: input, useIframe: changeHtmlSnippet ? 0 : 1, orderID: orderId})
      .then((response: string) => {
        setHtmlSnippet(response);
        setUpdateEmbedLink(true);
        setInvalidUrl(false);
        setLinkEmbeded(true);
      })
      .catch(() => {
        dispatch(setErrorOnPublishPanel(true));
        dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
      })
      .finally(() => {});
  };
  const handleSwapInnerHTML = (): void => {
    void window.PMW.writeLocal('poster/swapHtmlSnippetsNew', {hid: hashedId, orderID: orderId, useIframe: changeHtmlSnippet ? 1 : 0})
      .then((response: PublishingEmbedResponseProps) => {
        setHtmlSnippet(response.htmlSnippet);
      })
      .catch(() => {
        dispatch(setErrorOnPublishPanel(true));
        dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
      })
      .finally(() => {
        setChangeHtmlSnippet(!changeHtmlSnippet);
        setCopied(false);
      });
  };

  const handleCopy = (): void => {
    window.PMW.copyTextToClipboard(htmlSnippet);
    setCopied(true);
  };

  const handleSimpleHTMLSnippet = (): ReactElement => {
    return (
      <div className="flex-center">
        <Text
          val={changeHtmlSnippet ? window.i18next.t('pmwjs_webpage_embed_html_snippet_recommended') : window.i18next.t('pmwjs_webpage_embed_html_snippet_simple')}
          className={`${changeHtmlSnippet ? 'color-danger-100' : 'content-link'} body-xxs _cursor-pointer`}
          underline
          onClick={handleSwapInnerHTML}
        />
      </div>
    );
  };
  const getHTMLSnippet = (): ReactElement => {
    return (
      <div className={styles.handleHTMLSnippetSpacing}>
        <div className="flexbox align-items-center content-body">
          <Text val={window.i18next.t('pmwjs_webpage_embed_html_snippet')} className="body-xs-bold content-body" />
          {changeHtmlSnippet ? <Text val={window.i18next.t('pmwjs_webpage_embed_simplified')} className="spacing-p-l-2" size={TextSize.XSMALL} /> : ''}
          <OverlayTrigger
            trigger={['hover', 'focus', 'click']}
            overlay={<Tooltip className="spacing-m-b-2">{window.i18next.t('pmwjs_webpage_embed_info')}</Tooltip>}
            delay={{show: 200, hide: 200}}
            placement="top"
            popperConfig={undefined}
            show={undefined}
            defaultShow={undefined}
            onHide={undefined}
            onToggle={undefined}
            target={undefined}
            flip={undefined}
            offset={undefined}
          >
            <div className="flexbox">
              <i className="icon size-icon-16 icon-info" />
            </div>
          </OverlayTrigger>
        </div>
        <div>
          <InputField isDisabled inputClassName={published ? '' : styles.disableColorHTML} hasIcon="icon-code" placeholder=" " input={htmlSnippet} />
        </div>
        <div className="flexbox">
          <Text
            val={changeHtmlSnippet ? window.i18next.t('pmwjs_webpage_embed_link_wont_work') : window.i18next.t('pmwjs_webpage_embed_trouble')}
            className={`${changeHtmlSnippet ? 'color-danger-100' : 'content-sub-text'} body-xxs spacing-p-r-1`}
          />
          {handleSimpleHTMLSnippet()}
        </div>
      </div>
    );
  };
  const getCopyButton = (): ReactElement => {
    return copied ? (
      <OverlayTrigger
        trigger={['hover', 'focus', 'click']}
        placement="top"
        delay={{show: 50, hide: 100}}
        overlay={
          <Tooltip id="tooltip" className="_pointer-events-none spacing-m-b-1">
            {window.i18next.t('pmwjs_link_copied')}
          </Tooltip>
        }
        show={undefined}
        defaultShow={undefined}
        flip={undefined}
        onHide={undefined}
        onToggle={undefined}
        popperConfig={undefined}
        target={undefined}
      >
        <Button
          type={Type.SUCCESS}
          size={Size.SMALL}
          iconPlacement={IconPlacement.LEFT}
          text={published ? window.i18next.t('pmwjs_insights_keywords_copied') : ''}
          icon={published ? 'icon-check' : `icon-copy`}
          disabled={!published}
        />
      </OverlayTrigger>
    ) : (
      <Button
        onClick={handleCopy}
        type={published ? Type.PRIMARY : Type.SECONDARY}
        size={Size.SMALL}
        iconPlacement={IconPlacement.LEFT}
        text={window.i18next.t('pmwjs_publish_copy_code')}
        icon="icon-copy"
        disabled={!published}
      />
    );
  };
  const getButtonsForEmbedOptions = (): ReactElement => {
    return (
      <div className={`${styles.handleButtonsSpacing}`}>
        {published ? (
          <Button
            type={Type.SECONDARY}
            size={Size.SMALL}
            iconPlacement={IconPlacement.LEFT}
            text={window.i18next.t('pmwjs_webpage_embed_disabled')}
            iconClassName={styles.disableColor}
            textClasses="content-disabled-dark"
            icon="icon-ban"
            onClick={handleEmbedUnpublishClick}
            isLoading={isLoading}
          />
        ) : (
          <Button
            type={Type.SECONDARY}
            size={Size.SMALL}
            iconPlacement={IconPlacement.LEFT}
            text={window.i18next.t('pmwjs_webpage_embed_enable')}
            iconClassName=""
            textClasses=""
            icon="icon-check"
            onClick={handleEmbedPublishClick}
            isLoading={isLoading}
          />
        )}
        {getCopyButton()}
      </div>
    );
  };
  const addLinkButton = (): ReactElement => {
    return (
      <div>
        {!addLink ? (
          <Button
            onClick={() => {
              setAddLink(true);
              setInput('');
            }}
            type={Type.DASHED}
            size={Size.SMALL}
            iconPlacement={IconPlacement.LEFT}
            iconClassName="icon"
            text="Add Link"
            icon="icon-plus"
            isFullWidth
          />
        ) : (
          <InputField
            inputType={InputFieldType.TEXT}
            hasIcon="icon-link"
            useEmptyPlaceholder
            inputClassName={styles.disableColorHTML}
            iconClassName={styles.linkColor}
            input={input}
            isReadOnly={updateEmbedLink}
            onInputChange={(updatedInput: string): void => {
              setInput(updatedInput);
            }}
            controls={{
              showDefaultControls: false,
              showSingleDefaultControlOnly: false,
              customControls: updateEmbedLink
                ? [
                    {
                      id: 'icon-pencil-line',
                      type: InputControlItemType.ICON_BTNS,
                      icon: 'icon-pencil-line',
                      className: 'content-body size-icon-20',
                    },
                    {
                      id: 'icon-delete',
                      type: InputControlItemType.ICON_BTNS,
                      icon: 'icon-delete',
                      className: 'content-danger size-icon-20',
                    },
                  ]
                : [
                    {
                      id: 'icon-check',
                      type: InputControlItemType.ICON_BTNS,
                      icon: 'icon-check',
                      className: 'content-success size-icon-20',
                    },
                    {
                      id: 'icon-close',
                      type: InputControlItemType.ICON_BTNS,
                      icon: 'icon-close',
                      className: 'content-danger size-icon-20',
                    },
                  ],

              onClick(iconName: string) {
                if (iconName === 'icon-close') {
                  if (linkEmbeded) {
                    setUpdateEmbedLink(true);
                    setInput(oldInput);
                  } else {
                    setAddLink(false);
                  }
                  setInvalidUrl(false);
                } else if (iconName === 'icon-check') {
                  handleUpdateEmbedLink();
                } else if (iconName === 'icon-pencil-line') {
                  setOldInput(input);
                  setUpdateEmbedLink(false);
                  setInvalidUrl(false);
                } else if (iconName === 'icon-delete') {
                  handleRemoveEmbedLink();
                }
              },
            }}
          />
        )}
      </div>
    );
  };
  const handleLinkAddition = (): ReactElement => {
    return (
      <div className={styles.handleLinkAdditionSpacing}>
        <div className="flexbox align-items-center content-body">
          <Text val={window.i18next.t('pmwjs_webpage_embed_link')} className="body-xs-bold content-body" />
          <OverlayTrigger
            trigger={['hover', 'focus', 'click']}
            overlay={<Tooltip className="spacing-m-b-2">{window.i18next.t('pmwjs_webpage_embed_link_info')}</Tooltip>}
            delay={{show: 200, hide: 200}}
            placement="top"
            popperConfig={undefined}
            show={undefined}
            defaultShow={undefined}
            onHide={undefined}
            onToggle={undefined}
            target={undefined}
            flip={undefined}
            offset={undefined}
          >
            <div className="flexbox">
              <i className="icon size-icon-16 icon-info" />
            </div>
          </OverlayTrigger>
        </div>
        {addLinkButton()}
      </div>
    );
  };

  if (inMobile) {
    panelStyle = styles.handleSpacingPanelMobile;
  } else {
    panelStyle = displayType === DisplayType.MODAL ? styles.handleSpacingPanel : styles.handleSpacingDropDown;
  }

  const getPanelContent = (): ReactElement => {
    return showLoadScreen ? (
      <PublishingLoaderScreen customHeight={displayType === DisplayType.MODAL ? styles.setCustomHeightModal : styles.setCustomHeightDropDown} loaderSize={LOADER_SIZE.LARGE} />
    ) : (
      <>
        <div>
          {getHTMLSnippet()}
          {getButtonsForEmbedOptions()}
        </div>

        <div className={published ? '' : '_hidden'}>
          <div>{handleLinkAddition()}</div>
          {invalidUrl ? (
            <div className="spacing-p-t-1">
              <Text className="color-danger-100" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_enter_valid_url')} />
            </div>
          ) : (
            ''
          )}
        </div>
      </>
    );
  };

  return (
    <div>
      <div className={showLoader ? '' : panelStyle}>{showLoader ? <PublishingProgressBar hashedId={hashedId} /> : getPanelContent()}</div>
    </div>
  );
}
